import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowPathIcon, BanknotesIcon, DocumentTextIcon, ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import { fetchBuyerInvoices, downloadBuyerInvoicePdf } from '../api/marketplaceApi';

const BuyerInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchBuyerInvoices();
      setInvoices(response.invoices || response.items || response.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownloadPdf = async (invoice: any) => {
    setDownloadingId(invoice.id);
    try {
      await downloadBuyerInvoicePdf(invoice.id, invoice.invoice_number || `INV-${invoice.id}`);
    } catch (err: any) {
      alert(err.message || 'Failed to download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  const totalOutstanding = invoices
    .filter(i => i.status === 'issued' || i.status === 'viewed' || i.status === 'overdue' || i.status === 'pending' || i.status === 'partially_paid')
    .reduce((sum, i) => sum + (i.balance_due || i.total_amount || 0), 0);

  const totalPaid = invoices
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + (i.total_amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="relative">
          <div className="w-10 h-10 border-4 border-emerald-200 rounded-full"></div>
          <div className="w-10 h-10 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-red-700 font-medium mt-2">{error}</p>
        <button onClick={fetchData} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Invoices & Payments</h2>
          <p className="text-sm text-gray-500 mt-0.5">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Total Invoices</p>
          <p className="text-2xl font-extrabold text-gray-900 mt-1">{invoices.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Outstanding</p>
          <p className="text-2xl font-extrabold text-amber-600 mt-1"><Money amount={totalOutstanding} /></p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Paid</p>
          <p className="text-2xl font-extrabold text-emerald-600 mt-1"><Money amount={totalPaid} /></p>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-xl p-12 text-center">
          <BanknotesIcon className="w-12 h-12 text-gray-300 mx-auto" />
          <p className="text-gray-500 mt-3 font-medium">No invoices yet</p>
          <p className="text-gray-400 text-sm mt-1">Invoices will appear here once orders are fulfilled.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Invoice</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Order</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Amount</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Paid</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Balance</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Status</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Due Date</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {invoices.map((inv: any) => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-xs font-medium">{inv.invoice_number || `INV-${inv.id}`}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{inv.order_number || inv.supplier_order_number || '—'}</td>
                  <td className="px-4 py-3 font-bold"><Money amount={inv.total_amount || 0} currency={inv.currency} /></td>
                  <td className="px-4 py-3 text-gray-600"><Money amount={inv.amount_paid || 0} currency={inv.currency} /></td>
                  <td className="px-4 py-3 font-medium">
                    <Money amount={inv.balance_due ?? (inv.total_amount - (inv.amount_paid || 0))} currency={inv.currency} />
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status || 'pending'} size="sm" /></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDownloadPdf(inv)}
                      disabled={downloadingId === inv.id}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                      {downloadingId === inv.id ? 'Downloading...' : 'PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BuyerInvoices;
